import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWelcomeOffer, useWelcomeCountdown, WelcomePrice } from './WelcomeOfferContext';
import { subscriptionService } from '../api/subscriptionService';
import { detectCountry, getDisplayCurrency, getPlanPrice, getCachedCountry } from '../utils/pricing';
import { useI18n, rich } from '../i18n';
import './WelcomeOfferBanner.css';

export default function WelcomeOfferBanner() {
  const { offer } = useWelcomeOffer();
  const { t } = useI18n();
  const remainingSeconds = useWelcomeCountdown();
  const bannerRef = useRef(null);
  const dialogRef = useRef(null);
  const claimRef = useRef(null);
  const [showPlans, setShowPlans] = useState(false);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState('');
  const [currency, setCurrency] = useState(() => getDisplayCurrency({ geoCountry: getCachedCountry() }));
  const active = Boolean(offer);

  useEffect(() => {
    if (!active) return undefined;
    document.body.classList.add('welcome-offer-visible');
    const measure = () => document.documentElement.style.setProperty('--welcome-banner-height', `${bannerRef.current?.offsetHeight || 0}px`);
    measure();
    const observer = new ResizeObserver(measure);
    if (bannerRef.current) observer.observe(bannerRef.current);
    return () => {
      observer.disconnect();
      document.body.classList.remove('welcome-offer-visible');
      document.documentElement.style.removeProperty('--welcome-banner-height');
    };
  }, [active]);

  useEffect(() => {
    if (!showPlans) return undefined;
    let cancelled = false;
    detectCountry().then(country => { if (!cancelled) setCurrency(getDisplayCurrency({ geoCountry: country })); });
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => { cancelled = true; dialog?.close(); };
  }, [showPlans]);

  const close = () => {
    if (pending) return;
    setShowPlans(false);
    claimRef.current?.focus();
  };
  const checkout = async (plan) => {
    if (!offer || pending) return;
    setPending(plan);
    setError('');
    try {
      const result = await subscriptionService.createCheckoutSession(plan, currency, offer.percent_off);
      if (!result?.checkout_url) throw new Error(t('Checkout is unavailable. Please try again.'));
      window.location.assign(result.checkout_url);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || t('Unable to open checkout. Please try again.'));
      setPending(null);
      window.dispatchEvent(new Event('subscription-changed'));
    }
  };
  const timer = `${String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:${String(remainingSeconds % 60).padStart(2, '0')}`;

  return <>
    {offer && <aside className="welcome-banner" ref={bannerRef} aria-label={t('Your welcome offer')}>
      <span className="welcome-banner-badge">{t('JUST JOINED')}</span>
      <div className="welcome-banner-copy"><strong>{t('Your first month, {percent}% off.', { percent: offer.percent_off })}</strong><span>{t('PRO & MAX · One welcome offer. Start checkout before time runs out.')}</span></div>
      <div className="welcome-banner-clock"><span>{t('Claim within')}</span><strong role="timer" dir="ltr" aria-label={t('{seconds} seconds left to claim', { seconds: remainingSeconds })}>{timer}</strong></div>
      <button ref={claimRef} onClick={() => { setError(''); setShowPlans(true); }}>{t('Unlock my offer')} <span aria-hidden="true" className="welcome-banner-arrow">↗</span></button>
    </aside>}
    {showPlans && createPortal(<dialog ref={dialogRef} className="welcome-dialog" aria-labelledby="welcome-dialog-title" onCancel={event => { event.preventDefault(); close(); }}>
      <button className="welcome-dialog-close" aria-label={t('Close welcome offer')} disabled={Boolean(pending)} onClick={close}>×</button>
      <p className="welcome-dialog-eyebrow">{t('YOUR WELCOME OFFER')}</p>
      <h2 id="welcome-dialog-title">{offer ? t('Make your first month count. Save {percent}%.', { percent: offer.percent_off }) : t('Your welcome offer has ended.')}</h2>
      {offer ? <>
        <p>{rich(t('Start checkout within <b>{timer}</b> to claim. Your discount is applied automatically.', { timer }), { b: (c) => <strong dir="ltr">{c}</strong> })}</p>
        <div className="welcome-dialog-plans">{['pro', 'max'].map(plan => <section key={plan}>
          <h3>{plan.toUpperCase()}</h3>
          <div className="welcome-dialog-price"><WelcomePrice plan={plan} price={getPlanPrice(plan, currency)} /></div>
          <p>{t('{count} videos per month', { count: plan === 'pro' ? 30 : 100 })}</p>
          <button disabled={Boolean(pending)} onClick={() => checkout(plan)}>{pending === plan ? t('Opening checkout…') : t('Choose {plan}', { plan: plan.toUpperCase() })}</button>
        </section>)}</div>
        <p className="welcome-dialog-fine">{t('First month only. Renews at the regular monthly price shown above. Cancel anytime in Settings. Once claimed, you have 30 minutes to complete Stripe checkout.')}</p>
      </> : <p>{t('This one-time offer does not restart. You can still explore PRO and MAX in Settings.')}</p>}
      {error && <p role="alert" className="welcome-dialog-error">{error}</p>}
    </dialog>, document.body)}
  </>;
}
